// ── Double-entry ledger — posting helper + seed chart of accounts ──────────
//
// The ledger is ADDITIVE. Every existing scalar (invoice.paidUSD,
// bankAccount.currentBalance, inventory.costUSD/costingUSD) stays as-is and
// remains what the current UI reads; a balanced JournalEntry is written
// alongside it inside the SAME runTransaction as the business write.
//
// This module is deliberately NOT a hook — buildEntry/postEntry/reverseEntry
// are pure functions callable inside `runTransaction(db, async tx => …)` after
// the transaction's read phase. seedChartOfAccounts is a one-time bulk create
// (idempotent) run from Settings → Accounting or the Chart of Accounts
// empty state.
//
// Phase A1 lands the types, the seed, and the posting helpers. Nothing calls
// postEntry/reverseEntry yet — live posting + backfill is Phase A2.
// See PHASES.md "Architecture > A. The ledger".

import { paidGstPosting, brokerGstOf, type PaidGstLike } from './tradingSystem';
import {
  collection, doc, getDocs, writeBatch,
  type Firestore, type Transaction,
} from 'firebase/firestore';
import type {
  JournalEntry, JournalLine, JournalSourceType, LedgerAccountType, NormalBalance,
  PurchaseInvoice, SaleInvoice, SaleLineItem, PurchasePayment, SalePayment, BrokerPayment,
} from '../types';

// Firestore rejects `undefined` in a write — drop those keys (null is fine).
// Duplicated per-file by convention across this codebase.
function stripUndef<T>(obj: T): T {
  if (Array.isArray(obj)) return obj.map(v => stripUndef(v)) as unknown as T;
  if (obj !== null && typeof obj === 'object' && (obj as object).constructor === Object) {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, stripUndef(v)])
    ) as T;
  }
  return obj;
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

// ── Account slugs (deterministic ledgerAccounts/{id}) ──────────────────────
// Posting code references A.ap etc. so a typo is a compile error, not a
// silently-misposted entry.
export const A = {
  cashOnHand:           'cash-on-hand',
  ar:                   'ar',
  supplierAdvances:     'supplier-advances',
  inventoryOwned:       'inventory-owned',
  inventoryTransit:     'inventory-transit',
  consignedStock:       'consigned-stock',
  consignedStockOut:    'consigned-stock-out',
  inputVat:             'input-vat',
  ap:                   'ap',
  customerAdvances:     'customer-advances',
  outputVat:            'output-vat',
  brokerPayable:        'broker-payable',
  accruedExpenses:      'accrued-expenses',
  consignmentLiability: 'consignment-liability',
  ownerCapital:         'owner-capital',
  openingBalanceEquity: 'opening-balance-equity',
  retainedEarnings:     'retained-earnings',
  salesRevenue:         'sales-revenue',
  salesReturns:         'sales-returns',
  purchaseDiscounts:    'purchase-discounts',
  fxGain:               'fx-gain',
  otherIncome:          'other-income',
  cogs:                 'cogs',
  invAdjLoss:           'inv-adj-loss',
  invAdjGain:           'inv-adj-gain',
  invWriteoff:          'inv-writeoff',
  purchaseExpenses:     'purchase-expenses',
  salesExpenses:        'sales-expenses',
  bankCharges:          'bank-charges',
  brokerExpense:        'broker-expense',
  fxLoss:               'fx-loss',
} as const;

/** Ledger slug for the cash/bank account that mirrors a bankAccounts doc. */
export const bankAccountSlug = (bankAccountId: string) => `bank-${bankAccountId}`;

// ── Operating-expense & income categories ────────────────────────────────
// Single source of truth (Bank.tsx's AddEntryModal + the Expenses/Income
// pages all import from here). Each expense category gets its own
// `opex-<slug>` ledger account, seeded by seedChartOfAccounts.

export const EXPENSE_CATEGORIES = [
  'Rent / Office', 'Electricity', 'Internet & Phone', 'Water',
  'Salaries & Wages', 'Grading Fees (GIA/IGI/HRD)',
  'Shipping & Courier', 'Custom Duty & Taxes', 'Insurance',
  'Travel & Transport', 'Meals & Entertainment', 'Bank Charges',
  'Professional Fees', 'Miscellaneous Expense',
] as const;

export const INCOME_CATEGORIES = [
  'Interest Earned', 'Refund / Credit Note', 'Miscellaneous Income',
] as const;

function catSlug(cat: string): string {
  return cat.toLowerCase()
    .replace(/[()/&]/g, ' ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

/** Ledger account for an expense category. "Bank Charges" reuses the seeded
 *  5400 account; everything else routes to its own `opex-<slug>` (or
 *  `opex-other` for an unknown/blank category). */
export function expenseAccountId(category: string): string {
  if (category === 'Bank Charges') return A.bankCharges;
  const slug = catSlug(category || '');
  return slug ? `opex-${slug}` : 'opex-other';
}

/** A4: manual income all lands in Other Income; the category string is still
 *  stored on the bank transaction for reporting. */
export function incomeAccountId(): string {
  return A.otherIncome;
}

// ── Seed chart of accounts ────────────────────────────────────────────────

interface SeedAccount {
  slug: string;
  code: string;
  name: string;
  type: LedgerAccountType;
  normalBalance: NormalBalance;
  sortOrder: number;
}

export const SEED_COA: SeedAccount[] = [
  { slug: A.cashOnHand,           code: '1000', name: 'Cash on Hand',                     type: 'asset',     normalBalance: 'debit',  sortOrder: 100 },
  { slug: A.ar,                   code: '1100', name: 'Accounts Receivable',              type: 'asset',     normalBalance: 'debit',  sortOrder: 110 },
  { slug: A.supplierAdvances,     code: '1150', name: 'Supplier Advances (prepaid)',      type: 'asset',     normalBalance: 'debit',  sortOrder: 115 },
  { slug: A.inventoryOwned,       code: '1200', name: 'Inventory – Owned',                type: 'asset',     normalBalance: 'debit',  sortOrder: 120 },
  { slug: A.inventoryTransit,     code: '1210', name: 'Inventory – In Transit',           type: 'asset',     normalBalance: 'debit',  sortOrder: 121 },
  { slug: A.consignedStock,       code: '1250', name: 'Consigned Stock (Memo In)',        type: 'asset',     normalBalance: 'debit',  sortOrder: 125 },
  { slug: A.consignedStockOut,    code: '1260', name: 'Consigned Stock (Memo Out)',       type: 'asset',     normalBalance: 'debit',  sortOrder: 126 },
  { slug: A.inputVat,            code: '1300', name: 'Input VAT / GST Receivable',       type: 'asset',     normalBalance: 'debit',  sortOrder: 130 },
  { slug: A.ap,                   code: '2000', name: 'Accounts Payable',                 type: 'liability', normalBalance: 'credit', sortOrder: 200 },
  { slug: A.customerAdvances,     code: '2100', name: 'Customer Advances / Deposits',     type: 'liability', normalBalance: 'credit', sortOrder: 210 },
  { slug: A.outputVat,           code: '2200', name: 'Output VAT / GST Payable',         type: 'liability', normalBalance: 'credit', sortOrder: 220 },
  { slug: A.brokerPayable,       code: '2300', name: 'Broker Commission Payable',        type: 'liability', normalBalance: 'credit', sortOrder: 230 },
  { slug: A.accruedExpenses,     code: '2400', name: 'Accrued Expenses',                 type: 'liability', normalBalance: 'credit', sortOrder: 240 },
  { slug: A.consignmentLiability, code: '2500', name: 'Consignment Liability (Memo In)',  type: 'liability', normalBalance: 'credit', sortOrder: 250 },
  { slug: A.ownerCapital,        code: '3000', name: 'Owner Capital',                    type: 'equity',    normalBalance: 'credit', sortOrder: 300 },
  { slug: A.openingBalanceEquity, code: '3100', name: 'Opening Balance Equity',          type: 'equity',    normalBalance: 'credit', sortOrder: 310 },
  { slug: A.retainedEarnings,    code: '3900', name: 'Retained Earnings',                type: 'equity',    normalBalance: 'credit', sortOrder: 390 },
  { slug: A.salesRevenue,       code: '4000', name: 'Sales Revenue',                    type: 'income',    normalBalance: 'credit', sortOrder: 400 },
  { slug: A.salesReturns,       code: '4100', name: 'Sales Returns & Allowances',       type: 'income',    normalBalance: 'debit',  sortOrder: 410 },
  { slug: A.purchaseDiscounts,  code: '4200', name: 'Purchase Discounts Received',      type: 'income',    normalBalance: 'credit', sortOrder: 420 },
  { slug: A.fxGain,             code: '4900', name: 'FX Gain',                          type: 'income',    normalBalance: 'credit', sortOrder: 490 },
  { slug: A.otherIncome,       code: '4950', name: 'Other Income',                     type: 'income',    normalBalance: 'credit', sortOrder: 495 },
  { slug: A.cogs,              code: '5000', name: 'Cost of Goods Sold',               type: 'expense',   normalBalance: 'debit',  sortOrder: 500 },
  { slug: A.invAdjLoss,        code: '5100', name: 'Inventory Adjustment – Loss',       type: 'expense',   normalBalance: 'debit',  sortOrder: 510 },
  { slug: A.invAdjGain,        code: '5150', name: 'Inventory Adjustment – Gain',       type: 'expense',   normalBalance: 'credit', sortOrder: 515 },
  { slug: A.invWriteoff,       code: '5200', name: 'Inventory Write-off',               type: 'expense',   normalBalance: 'debit',  sortOrder: 520 },
  { slug: A.purchaseExpenses,  code: '5300', name: 'Purchase Expenses (freight/duty/cert)', type: 'expense', normalBalance: 'debit', sortOrder: 530 },
  { slug: A.salesExpenses,     code: '5350', name: 'Sales Expenses (shipping/insurance)', type: 'expense', normalBalance: 'debit', sortOrder: 535 },
  { slug: A.bankCharges,       code: '5400', name: 'Bank Charges',                      type: 'expense',   normalBalance: 'debit',  sortOrder: 540 },
  { slug: A.brokerExpense,     code: '5500', name: 'Broker Commission Expense',         type: 'expense',   normalBalance: 'debit',  sortOrder: 550 },
  { slug: A.fxLoss,            code: '5900', name: 'FX Loss',                           type: 'expense',   normalBalance: 'debit',  sortOrder: 590 },
];

export interface SeedResult { created: number; alreadyPresent: number }

/**
 * Writes just the SEED_COA system accounts missing from `haveIds` — for a
 * business whose chart was seeded before a new system account (e.g.
 * sales-expenses) was added. Called automatically by the ledger pages once
 * the chart exists; never touches existing or bank accounts.
 */
export async function addMissingSystemAccounts(db: Firestore, businessId: string, haveIds: Set<string>): Promise<number> {
  const missing = SEED_COA.filter(a => !haveIds.has(a.slug));
  if (missing.length === 0) return 0;
  const now = new Date().toISOString();
  const batch = writeBatch(db);
  for (const a of missing) {
    batch.set(doc(db, 'ledgerAccounts', a.slug), {
      id: a.slug, businessId, code: a.code, name: a.name, type: a.type,
      normalBalance: a.normalBalance, isSystem: true, isActive: true,
      sortOrder: a.sortOrder, createdAt: now,
    });
  }
  await batch.commit();
  return missing.length;
}

/**
 * Idempotent — writes any missing `ledgerAccounts/{slug}` from SEED_COA, plus
 * one `bank-<id>` asset account per active bankAccounts doc. Safe to re-run
 * ("Seed / Repair"). Never deletes or overwrites an existing account.
 */
export async function seedChartOfAccounts(
  db: Firestore,
  businessId: string,
  bankAccounts: { id: string; name: string; currency: string; type: string; isActive?: boolean }[],
): Promise<SeedResult> {
  const snap = await getDocs(collection(db, 'ledgerAccounts'));
  const have = new Set(snap.docs.map(d => d.id));
  const now = new Date().toISOString();
  const batch = writeBatch(db);
  let created = 0;

  for (const a of SEED_COA) {
    if (have.has(a.slug)) continue;
    batch.set(doc(db, 'ledgerAccounts', a.slug), stripUndef({
      id: a.slug, businessId, code: a.code, name: a.name, type: a.type,
      normalBalance: a.normalBalance, isSystem: true, isActive: true,
      sortOrder: a.sortOrder, createdAt: now,
    }));
    created++;
  }

  for (const acc of bankAccounts) {
    if (acc.isActive === false) continue;
    const slug = bankAccountSlug(acc.id);
    if (have.has(slug)) continue;
    batch.set(doc(db, 'ledgerAccounts', slug), stripUndef({
      id: slug, businessId,
      code: acc.type === 'cash' ? '1005' : '1010',
      name: `${acc.type === 'cash' ? 'Cash' : 'Bank'} – ${acc.name}`,
      type: 'asset' as LedgerAccountType, normalBalance: 'debit' as NormalBalance,
      isSystem: true, isActive: true, currency: acc.currency,
      linkedRefId: acc.id, sortOrder: 105, createdAt: now,
    }));
    created++;
  }

  // One expense account per operating-expense category, plus a catch-all.
  const opex: { slug: string; name: string }[] = [
    ...EXPENSE_CATEGORIES.filter(c => c !== 'Bank Charges').map(c => ({ slug: `opex-${catSlug(c)}`, name: c })),
    { slug: 'opex-other', name: 'Other Operating Expense' },
  ];
  opex.forEach(({ slug, name }, i) => {
    if (have.has(slug)) return;
    batch.set(doc(db, 'ledgerAccounts', slug), stripUndef({
      id: slug, businessId, code: `61${String(i).padStart(2, '0')}`, name,
      type: 'expense' as LedgerAccountType, normalBalance: 'debit' as NormalBalance,
      isSystem: true, isActive: true, sortOrder: 610 + i, createdAt: now,
    }));
    created++;
  });

  if (created > 0) await batch.commit();
  return { created, alreadyPresent: have.size };
}

// ── Entry building + posting ──────────────────────────────────────────────

export interface BuildEntryParams {
  businessId:        string;
  sourceType:        JournalSourceType;
  sourceId?:         string;
  sourceCollection?: string;
  entryNo?:          string;   // 'JE-2026-0001' — manual journal entries only
  date:              string;   // "YYYY-MM-DD"
  currency:          string;
  lines:             JournalLine[];
  note?:             string;
  fxRate?:           number;
  isReversal?:       boolean;
  reversalOf?:       string;
  createdBy:         string;
}

/**
 * Normalizes lines (rounds, drops zero/zero), asserts the entry balances
 * (Σ debit === Σ credit within 0.005), and returns the entry object ready for
 * `postEntry`. Throws `Ledger entry does not balance` on failure — the caller's
 * `runTransaction` then aborts, so a bad entry never half-commits.
 */
export function buildEntry(params: BuildEntryParams): Omit<JournalEntry, 'id'> {
  const lines = params.lines
    .map(l => ({
      accountId: l.accountId,
      debit:  round2(l.debit || 0),
      credit: round2(l.credit || 0),
      entityId: l.entityId,
      memo: l.memo,
    }))
    .filter(l => l.debit !== 0 || l.credit !== 0);

  if (lines.length < 2) {
    throw new Error('Ledger entry needs at least two non-zero lines.');
  }
  const totalDebit  = round2(lines.reduce((s, l) => s + l.debit, 0));
  const totalCredit = round2(lines.reduce((s, l) => s + l.credit, 0));
  if (Math.abs(totalDebit - totalCredit) > 0.005) {
    throw new Error(
      `Ledger entry does not balance (Dr ${totalDebit.toFixed(2)} vs Cr ${totalCredit.toFixed(2)}).`,
    );
  }

  return stripUndef({
    businessId:       params.businessId,
    date:             params.date,
    sourceType:       params.sourceType,
    sourceId:         params.sourceId,
    sourceCollection: params.sourceCollection,
    entryNo:          params.entryNo,
    lines,
    accountIds:       [...new Set(lines.map(l => l.accountId))],
    currency:         params.currency,
    fxRate:           params.fxRate,
    isReversal:       params.isReversal || undefined,
    reversalOf:       params.reversalOf,
    note:             params.note,
    createdAt:        new Date().toISOString(),
    createdBy:        params.createdBy,
  }) as Omit<JournalEntry, 'id'>;
}

/**
 * Writes the entry inside a transaction. Pass `deterministicId` (e.g.
 * `${sourceType}__${sourceId}`) when there is exactly one entry per source
 * doc — an edit then reverse+reposts against the same id, which also makes
 * the Phase A2 backfill idempotent. Omit it for many-per-source events
 * (ap_payment, manual journal). Returns the entry id.
 */
export function postEntry(
  tx: Transaction,
  db: Firestore,
  entry: Omit<JournalEntry, 'id'>,
  deterministicId?: string,
): string {
  const ref = deterministicId
    ? doc(db, 'journalEntries', deterministicId)
    : doc(collection(db, 'journalEntries'));
  tx.set(ref, stripUndef({ ...entry, id: ref.id }));
  return ref.id;
}

/** Deterministic entry id for the one-entry-per-source-doc case. */
export const entryId = (sourceType: JournalSourceType, sourceId: string) =>
  `${sourceType}__${sourceId}`;

/**
 * Writes a mirror entry with debit/credit swapped (`isReversal: true`,
 * `reversalOf` set). The caller is responsible for any
 * `tx.update(existingRef, { reversedBy })` it wants. Returns the new id.
 */
export function reverseEntry(
  tx: Transaction,
  db: Firestore,
  existing: JournalEntry,
  opts: { date?: string; createdBy: string; note?: string },
): string {
  const ref = doc(collection(db, 'journalEntries'));
  tx.set(ref, stripUndef({
    id:               ref.id,
    businessId:       existing.businessId,
    date:             opts.date ?? existing.date,
    sourceType:       existing.sourceType,
    sourceId:         existing.sourceId,
    sourceCollection: existing.sourceCollection,
    lines:            existing.lines.map(l => ({
      accountId: l.accountId, debit: l.credit, credit: l.debit,
      entityId: l.entityId, memo: l.memo,
    })),
    accountIds:       existing.accountIds ?? [...new Set(existing.lines.map(l => l.accountId))],
    currency:         existing.currency,
    fxRate:           existing.fxRate,
    isReversal:       true,
    reversalOf:       existing.id,
    note:             opts.note ?? `Reversal of ${existing.id}`,
    createdAt:        new Date().toISOString(),
    createdBy:        opts.createdBy,
  }));
  return ref.id;
}

// ── Line builders — one per money source type (Phase A2) ──────────────────
// The ledger is booked at ACTUAL cost (amountUSD / costUSD). The "added
// value %" costing overlay (costingUSD) is a management figure and never
// touches the GL — so purchase inventory-owned debit === sale COGS credit
// for the same stone and the inventory account nets to zero when it sells.

const dr = (accountId: string, amount: number, entityId?: string): JournalLine =>
  ({ accountId, debit: amount, credit: 0, entityId });
const cr = (accountId: string, amount: number, entityId?: string): JournalLine =>
  ({ accountId, debit: 0, credit: amount, entityId });

/**
 * Purchase invoice: we now own stock and owe the supplier. `amountUSD` is
 * what we owe (the invoice total); `taxUSD` (Indian GST) is the part of it
 * we can claim back, so stock gets only the rest.
 */
export const linesPurchaseInvoice = (amountUSD: number, supplierEntityId: string, taxUSD = 0): JournalLine[] =>
  taxUSD > 0.005
    ? [dr(A.inventoryOwned, round2(amountUSD - taxUSD)), dr(A.inputVat, taxUSD), cr(A.ap, amountUSD, supplierEntityId)]
    : [dr(A.inventoryOwned, amountUSD), cr(A.ap, amountUSD, supplierEntityId)];

/** Sale invoice: customer owes us revenue, and the sold stock leaves inventory at cost. */
// Indian GST (`taxUSD`) on top of the revenue: the customer owes it too, and
// it is owed on to the government (Output GST), never revenue.
export const linesSaleInvoice = (
  revenueUSD: number, costOfGoodsUSD: number, customerEntityId: string, taxUSD = 0,
): JournalLine[] => [
  dr(A.ar, round2(revenueUSD + (taxUSD > 0.005 ? taxUSD : 0)), customerEntityId),
  cr(A.salesRevenue, revenueUSD),
  ...(taxUSD > 0.005 ? [cr(A.outputVat, taxUSD)] : []),
  dr(A.cogs, costOfGoodsUSD),
  cr(A.inventoryOwned, costOfGoodsUSD),
];

/** Broker commission accrued on a sale (separate entry — settled independently). */
export const linesBrokerAccrual = (commissionUSD: number, brokerEntityId: string): JournalLine[] =>
  [dr(A.brokerExpense, commissionUSD), cr(A.brokerPayable, commissionUSD, brokerEntityId)];

/** Purchase return: stock goes back to the supplier, reducing what we owe.
 *  Indian GST (`taxUSD`) on the returned goods comes off too: we owe it
 *  less, and can no longer claim it (Cr Input GST). The purchase, reversed. */
export const linesPurchaseReturn = (costUSD: number, supplierEntityId: string, taxUSD = 0): JournalLine[] =>
  taxUSD > 0.005
    ? [dr(A.ap, round2(costUSD + taxUSD), supplierEntityId), cr(A.inventoryOwned, costUSD), cr(A.inputVat, taxUSD)]
    : [dr(A.ap, costUSD, supplierEntityId), cr(A.inventoryOwned, costUSD)];

/** Purchase discount granted on an invoice (settlement / price reduction) —
 *  what we owe drops, recorded as income. */
export const linesPurchaseDiscount = (amountUSD: number, supplierEntityId: string): JournalLine[] =>
  [dr(A.ap, amountUSD, supplierEntityId), cr(A.purchaseDiscounts, amountUSD)];

/** "Settle difference" on a sale: the small amount a customer paid short
 *  (bank rate, rounding, bank fee) is written off as FX Loss so the
 *  invoice closes. Dr FX Loss / Cr Accounts Receivable. */
export const linesSaleSettle = (amountUSD: number, customerEntityId: string): JournalLine[] =>
  [dr(A.fxLoss, amountUSD), cr(A.ar, amountUSD, customerEntityId)];

/** Purchase-side cost (freight / duty / certification). Capitalised → added
 *  to inventory value; expensed → straight to P&L. Credit side is a bank
 *  account (paid now) or Accrued Expenses (still owed). */
export const linesPurchaseExpense = (
  amount: number,
  mode: 'capitalise' | 'expense',
  credit: { bankAccountId: string } | 'accrued',
  vendorEntityId?: string,
): JournalLine[] => [
  dr(mode === 'capitalise' ? A.inventoryOwned : A.purchaseExpenses, amount, mode === 'capitalise' ? undefined : vendorEntityId),
  credit === 'accrued'
    ? cr(A.accruedExpenses, amount, vendorEntityId)
    : cr(bankAccountSlug(credit.bankAccountId), amount),
];

/**
 * Sale-side cost (shipping / insurance / export duty…). Our own cost hits
 * Sales Expenses; one re-billed to the customer is a receivable instead
 * (the sale's totalAmountUSD includes it), so it never touches Profit.
 */
export const linesSaleExpense = (
  amount: number,
  chargeToCustomer: boolean,
  credit: { bankAccountId: string } | 'accrued',
  customerEntityId: string,
  vendorEntityId?: string,
): JournalLine[] => [
  chargeToCustomer ? dr(A.ar, amount, customerEntityId) : dr(A.salesExpenses, amount, vendorEntityId),
  credit === 'accrued'
    ? cr(A.accruedExpenses, amount, vendorEntityId)
    : cr(bankAccountSlug(credit.bankAccountId), amount),
];

/** Supplier advance paid (money out, before an invoice exists). */
export const linesSupplierAdvance = (
  amount: number, supplierEntityId: string, bankAccountId: string,
): JournalLine[] => [
  dr(A.supplierAdvances, amount, supplierEntityId),
  cr(bankAccountSlug(bankAccountId), amount),
];
/** Applying a supplier advance to a purchase invoice — no cash moves. */
export const linesSupplierAdvanceApply = (
  amount: number, supplierEntityId: string,
): JournalLine[] => [
  dr(A.ap, amount, supplierEntityId),
  cr(A.supplierAdvances, amount, supplierEntityId),
];

/** Customer advance received (money in, before an invoice exists). */
export const linesCustomerAdvance = (
  amount: number, customerEntityId: string, bankAccountId: string,
): JournalLine[] => [
  dr(bankAccountSlug(bankAccountId), amount),
  cr(A.customerAdvances, amount, customerEntityId),
];
/** Applying a customer advance to a sale invoice — no cash moves. */
export const linesCustomerAdvanceApply = (
  amount: number, customerEntityId: string,
): JournalLine[] => [
  dr(A.customerAdvances, amount, customerEntityId),
  cr(A.ar, amount, customerEntityId),
];

/** Sales return / credit note: reverse revenue + AR at sale price, and put
 *  the stock back on the books at its original cost (reversing COGS). */
// Indian GST (`taxUSD`) on the credited goods: the customer is credited it
// too and we no longer owe it to the government (Dr Output GST).
export const linesSaleReturn = (
  revenueUSD: number, costUSD: number, customerEntityId: string, taxUSD = 0,
): JournalLine[] => [
  dr(A.salesReturns, revenueUSD),
  ...(taxUSD > 0.005 ? [dr(A.outputVat, taxUSD)] : []),
  cr(A.ar, round2(revenueUSD + (taxUSD > 0.005 ? taxUSD : 0)), customerEntityId),
  dr(A.inventoryOwned, costUSD),
  cr(A.cogs, costUSD),
];

/** Supplier payment: pay down AP from a bank/cash account. */
export const linesApPayment = (
  amount: number, supplierEntityId: string, bankAccountId: string,
): JournalLine[] => [dr(A.ap, amount, supplierEntityId), cr(bankAccountSlug(bankAccountId), amount)];

/** Customer receipt: money into a bank/cash account, AR paid down. */
export const linesArPayment = (
  amount: number, customerEntityId: string, bankAccountId: string,
): JournalLine[] => [dr(bankAccountSlug(bankAccountId), amount), cr(A.ar, amount, customerEntityId)];

/** Broker payout: settle broker-payable from a bank/cash account. */
// Indian GST on brokerage (`gstUSD`, from paidGstPosting-style flags): the
// bank also pays the GST (unless reverse charge, where we owe it ourselves).
// Claimable GST goes to Input GST, else it is extra broker expense.
export const linesBrokerPayment = (
  amount: number, brokerEntityId: string, bankAccountId: string,
  gst?: { taxUSD: number; claimable: boolean; reverseCharge: boolean },
): JournalLine[] => {
  const tax = gst && gst.taxUSD > 0.005 ? gst.taxUSD : 0;
  if (!tax) return [dr(A.brokerPayable, amount, brokerEntityId), cr(bankAccountSlug(bankAccountId), amount)];
  const taxDr = gst!.claimable ? dr(A.inputVat, tax) : dr(A.brokerExpense, tax, brokerEntityId);
  return gst!.reverseCharge
    ? [dr(A.brokerPayable, amount, brokerEntityId), cr(bankAccountSlug(bankAccountId), amount), taxDr, cr(A.outputVat, tax)]
    : [dr(A.brokerPayable, amount, brokerEntityId), taxDr, cr(bankAccountSlug(bankAccountId), round2(amount + tax))];
};

/** Operating expense. Credit side is a bank account (paid now) or Accrued
 *  Expenses (a bill you still owe). */
export const linesExpense = (
  amount: number, category: string,
  credit: { bankAccountId: string } | 'accrued',
  vendorEntityId?: string,
  // Indian GST (utils/tradingSystem.ts paidGstPosting). inputTaxUSD: GST
  // inside `amount` that can be claimed back (Input GST; only the rest is
  // the expense). reverseCharge: GST the seller didn't charge that we owe
  // ourselves: Cr Output GST, Dr Input GST (claimable) or Dr the expense.
  gst: { inputTaxUSD?: number; reverseCharge?: { taxUSD: number; claimable: boolean } } = {},
): JournalLine[] => {
  const inputTaxUSD = gst.inputTaxUSD ?? 0;
  const rc = gst.reverseCharge && gst.reverseCharge.taxUSD > 0.005 ? gst.reverseCharge : null;
  return [
    dr(expenseAccountId(category), inputTaxUSD > 0.005 ? round2(amount - inputTaxUSD) : amount, vendorEntityId),
    ...(inputTaxUSD > 0.005 ? [dr(A.inputVat, inputTaxUSD)] : []),
    credit === 'accrued'
      ? cr(A.accruedExpenses, amount, vendorEntityId)
      : cr(bankAccountSlug(credit.bankAccountId), amount),
    ...(rc ? [
      rc.claimable ? dr(A.inputVat, rc.taxUSD) : dr(expenseAccountId(category), rc.taxUSD, vendorEntityId),
      cr(A.outputVat, rc.taxUSD),
    ] : []),
  ];
};

/** Non-trading income received into a bank/cash account. A4 routes every
 *  category to Other Income (the category string still lives on the bank
 *  transaction for reporting). */
export const linesIncome = (
  amount: number, bankAccountId: string, sourceEntityId?: string,
): JournalLine[] => [
  dr(bankAccountSlug(bankAccountId), amount),
  cr(incomeAccountId(), amount, sourceEntityId),
];

/** A bank account's opening balance funds Opening Balance Equity. */
export const linesOpeningBank = (amount: number, bankAccountId: string): JournalLine[] =>
  [dr(bankAccountSlug(bankAccountId), amount), cr(A.openingBalanceEquity, amount)];

/**
 * Indian GST paid to the government (Bank › Pay GST, sourceType
 * 'tax_adjustment'). Input GST we hold is used first (set off), then the
 * rest of what we owe goes out of the bank: Dr Output GST (setoff + paid),
 * Cr Input GST setoff, Cr bank paid.
 */
export const linesGstPayment = (setoffUSD: number, paidUSD: number, bankAccountId: string): JournalLine[] => [
  dr(A.outputVat, round2(setoffUSD + paidUSD)),
  ...(setoffUSD > 0.005 ? [cr(A.inputVat, setoffUSD)] : []),
  ...(paidUSD > 0.005 ? [cr(bankAccountSlug(bankAccountId), paidUSD)] : []),
];

/** Transfer between two of our own accounts — nets to zero. */
export const linesBankTransfer = (amount: number, fromBankId: string, toBankId: string): JournalLine[] =>
  [dr(bankAccountSlug(toBankId), amount), cr(bankAccountSlug(fromBankId), amount)];

/** Cash deposited into a bank account (out of the cash drawer). */
export const linesBankDeposit = (amount: number, bankId: string): JournalLine[] =>
  [dr(bankAccountSlug(bankId), amount), cr(A.cashOnHand, amount)];

/** Cash withdrawn from a bank account (into the cash drawer). */
export const linesBankWithdrawal = (amount: number, bankId: string): JournalLine[] =>
  [dr(A.cashOnHand, amount), cr(bankAccountSlug(bankId), amount)];

/** A bank fee charged to an account. */
export const linesBankCharge = (amount: number, bankId: string): JournalLine[] =>
  [dr(A.bankCharges, amount), cr(bankAccountSlug(bankId), amount)];

/** Opening stock loaded onto the books — funded by Opening Balance Equity. */
export const linesOpeningStock = (totalCostUSD: number): JournalLine[] =>
  [dr(A.inventoryOwned, totalCostUSD), cr(A.openingBalanceEquity, totalCostUSD)];

/** Stock lost / shrunk in a count — value leaves inventory to a P&L loss. */
export const linesStockLoss = (costUSD: number): JournalLine[] =>
  [dr(A.invAdjLoss, costUSD), cr(A.inventoryOwned, costUSD)];

/** Stock found / gained in a count — value added to inventory from a P&L gain. */
export const linesStockGain = (costUSD: number): JournalLine[] =>
  [dr(A.inventoryOwned, costUSD), cr(A.invAdjGain, costUSD)];

/** Stock written off entirely — full remaining cost to a P&L write-off. */
export const linesStockWriteoff = (costUSD: number): JournalLine[] =>
  [dr(A.invWriteoff, costUSD), cr(A.inventoryOwned, costUSD)];

/** Stock revalued to a new carrying cost — signed delta on ACTUAL cost.
 *  Negative = written down (loss); positive = written up (gain). */
export const linesStockRevaluation = (deltaUSD: number): JournalLine[] =>
  deltaUSD >= 0
    ? [dr(A.inventoryOwned, deltaUSD), cr(A.invAdjGain, deltaUSD)]
    : [dr(A.invAdjLoss, -deltaUSD), cr(A.inventoryOwned, -deltaUSD)];

/** Consigned (memo-in) stock held but not owned — a Balance-Sheet-only pair
 *  that nets to zero. `heldCostUSD` is the running total of still-held lines;
 *  when it reaches 0 the caller deletes the entry rather than posting this. */
export const linesMemoIn = (heldCostUSD: number): JournalLine[] =>
  [dr(A.consignedStock, heldCostUSD), cr(A.consignmentLiability, heldCostUSD)];

/** Consigned (memo-out) stock — still OUR asset, just reclassified from
 *  "Inventory – Owned" to "Consigned Stock (Memo Out)" while it's physically
 *  with a customer on memo. Unlike memo-in, this is an asset↔asset move (no
 *  liability) since we haven't lost ownership — no revenue is recognised
 *  until it actually sells. Sized at cost (`costUSD`, never `costingUSD` —
 *  see the note above `dr`/`cr`), posted once at send time; CustomerMemo is
 *  a point-in-time snapshot (not live-synced to partial returns/sales), so
 *  this entry is only ever fully removed on manual "Close Consignment", not
 *  resized mid-lifecycle the way linesMemoIn's heldCostUSD is. */
export const linesMemoOut = (costUSD: number): JournalLine[] =>
  [dr(A.consignedStockOut, costUSD), cr(A.inventoryOwned, costUSD)];

const saleCogs = (lineItems: Pick<SaleLineItem, 'costUSD'>[]) =>
  round2(lineItems.reduce((s, li) => s + (li.costUSD || 0), 0));

// ── Rebuild ledger from history ───────────────────────────────────────────

export interface RebuildResult {
  entries:      number;
  totalDebit:   number;
  totalCredit:  number;
  balanced:     boolean;
  seeded:       number;
}

/**
 * Idempotent client-side backfill: re-derives one journal entry per existing
 * purchase invoice, sale invoice, broker accrual, and every AP/AR/broker
 * payment, using the deterministic ids `${sourceType}__${sourceId}`. Re-running
 * overwrites in place, so it is always safe to run again. Seeds any missing
 * ledger account first. Does NOT delete orphaned entries (whose source doc was
 * removed before delete-side posting shipped) — a rare, harmless case.
 *
 * `startDate` (YYYY-MM-DD) skips docs dated before it — history simply isn't
 * posted rather than collapsed into an opening entry (that comes with the
 * Opening Balances work in a later phase).
 */
export async function rebuildLedgerFromHistory(
  db: Firestore,
  businessId: string,
  currency: string,
  createdBy: string,
  opts?: { startDate?: string },
): Promise<RebuildResult> {
  const start = opts?.startDate || '';
  const onOrAfter = (d?: string) => !start || (!!d && d >= start);

  // 0 — make sure the chart of accounts exists.
  const bankSnap = await getDocs(collection(db, 'bankAccounts'));
  const banks = bankSnap.docs.map(d => ({ id: d.id, ...d.data() } as { id: string; name: string; currency: string; type: string; isActive?: boolean }));
  const seed = await seedChartOfAccounts(db, businessId, banks);

  type PlannedEntry = { id: string; entry: Omit<JournalEntry, 'id'> };
  const planned: PlannedEntry[] = [];
  const push = (
    sourceType: JournalSourceType, sourceId: string, sourceCollection: string,
    date: string, lines: JournalLine[], note: string,
    // Only for the one-invoice-many-brokers case (per-line Broker1/Broker2):
    // the doc id needs the composite `${invoiceId}_${brokerEntityId}` form
    // (matching Purchase.tsx's/Sales.tsx's own lineBrokerAccrualId) so
    // several brokers on the same invoice don't overwrite each other, but
    // the entry's own `sourceId` field should stay the clean invoice id —
    // same as every other entry's — for correct source-invoice linking.
    docIdOverride?: string,
  ) => {
    try {
      planned.push({
        id: docIdOverride ?? entryId(sourceType, sourceId),
        entry: buildEntry({ businessId, sourceType, sourceId, sourceCollection, date, currency, createdBy, lines, note }),
      });
    } catch { /* unbalanced / degenerate source row — skip it rather than abort the whole rebuild */ }
  };
  // Mirrors Purchase.tsx's/Sales.tsx's own lineBrokerCommissions /
  // lineBrokerAccrualId (duplicated per this app's per-file-helper
  // convention — ledger.ts is a shared util, not a page, but this is a
  // small pure calculation, not meaningful transactional logic worth a
  // shared export).
  const lineBrokerCommissions = (
    lineItems: { amountUSD: number; grossAmountUSD?: number; broker1EntityId?: string; broker1CommissionPercent?: number; broker2EntityId?: string; broker2CommissionPercent?: number }[],
  ): Map<string, number> => {
    const totals = new Map<string, number>();
    for (const li of lineItems) {
      if (li.broker1EntityId && li.broker1CommissionPercent) {
        const amt = round2((li.grossAmountUSD ?? li.amountUSD ?? 0) * li.broker1CommissionPercent / 100);
        totals.set(li.broker1EntityId, round2((totals.get(li.broker1EntityId) ?? 0) + amt));
      }
      if (li.broker2EntityId && li.broker2CommissionPercent) {
        const amt = round2((li.grossAmountUSD ?? li.amountUSD ?? 0) * li.broker2CommissionPercent / 100);
        totals.set(li.broker2EntityId, round2((totals.get(li.broker2EntityId) ?? 0) + amt));
      }
    }
    return totals;
  };
  const lineBrokerAccrualId = (invoiceId: string, brokerEntityId: string) =>
    entryId('broker_accrual', `${invoiceId}_${brokerEntityId}`);

  // 1 — purchase invoices + their payments.
  const invSnap = await getDocs(collection(db, 'invoices'));
  for (const d of invSnap.docs) {
    const inv = { id: d.id, ...d.data() } as PurchaseInvoice;
    const invDate = inv.purchaseDate || inv.dueDate || '';
    if (onOrAfter(invDate) && (inv.totalAmountUSD ?? 0) > 0.005) {
      push('purchase_invoice', inv.id, 'invoices', invDate || start || today(),
        linesPurchaseInvoice(inv.totalAmountUSD, inv.fromEntityId, inv.taxTotalUSD ?? 0), `Purchase ${inv.invoiceNo}`);
    }
    const paySnap = await getDocs(collection(db, 'invoices', inv.id, 'payments'));
    for (const p of paySnap.docs) {
      const pay = { id: p.id, ...p.data() } as PurchasePayment;
      const amt = pay.amount ?? 0;
      if (!pay.bankAccountId || amt <= 0.005 || !onOrAfter(pay.paidDate)) continue;
      push('ap_payment', pay.id, `invoices/${inv.id}/payments`, pay.paidDate || invDate || today(),
        linesApPayment(amt, inv.fromEntityId, pay.bankAccountId), `Payment for ${inv.invoiceNo}`);
    }
    const adjSnap = await getDocs(collection(db, 'invoices', inv.id, 'adjustments'));
    for (const a of adjSnap.docs) {
      const adj = { id: a.id, ...a.data() } as { id: string; date: string; amountUSD?: number };
      if ((adj.amountUSD ?? 0) <= 0.005 || !onOrAfter(adj.date)) continue;
      push('purchase_discount', adj.id, `invoices/${inv.id}/adjustments`, adj.date || invDate || today(),
        linesPurchaseDiscount(adj.amountUSD!, inv.fromEntityId), `Discount on ${inv.invoiceNo}`);
    }
    // Buyer-paid broker commission on this purchase — accrual + payouts,
    // mirroring the Sales-side broker handling below.
    if (inv.brokerEntityId && (inv.brokerCommissionUSD ?? 0) > 0.005 && onOrAfter(invDate)) {
      push('broker_accrual', inv.id, 'invoices', invDate || start || today(),
        linesBrokerAccrual(inv.brokerCommissionUSD!, inv.brokerEntityId), `Broker commission — ${inv.invoiceNo}`);
    }
    // Per-line Broker1/Broker2 accruals — alongside the header broker above,
    // one entry per distinct broker found across the line items (mirrors
    // Purchase.tsx's own createPurchaseInvoice/edit-path posting).
    for (const [brokerEntityId, commissionUSD] of lineBrokerCommissions(inv.lineItems ?? [])) {
      if (commissionUSD <= 0.005 || !onOrAfter(invDate)) continue;
      push('broker_accrual', inv.id, 'invoices', invDate || start || today(),
        linesBrokerAccrual(commissionUSD, brokerEntityId), `Broker commission (per-line) — ${inv.invoiceNo}`,
        lineBrokerAccrualId(inv.id, brokerEntityId));
    }
    const invBpaySnap = await getDocs(collection(db, 'invoices', inv.id, 'brokerPayments'));
    for (const p of invBpaySnap.docs) {
      const pay = { id: p.id, ...p.data() } as BrokerPayment;
      const amt = pay.amount ?? 0;
      // A per-line payment's own brokerEntityId takes priority (several
      // brokers can exist on one invoice); a legacy/header payment has none,
      // so it falls back to the invoice's single header-level broker.
      const payBrokerEntityId = pay.brokerEntityId ?? inv.brokerEntityId;
      if (!pay.bankAccountId || amt <= 0.005 || !payBrokerEntityId || !onOrAfter(pay.paidDate)) continue;
      push('broker_payment', pay.id, `invoices/${inv.id}/brokerPayments`, pay.paidDate || invDate || today(),
        linesBrokerPayment(amt, payBrokerEntityId, pay.bankAccountId, brokerGstOf(pay)), `Broker payout — ${inv.invoiceNo}`);
    }
  }

  // 2 — sale invoices, broker accruals, receipts, broker payouts.
  const saleSnap = await getDocs(collection(db, 'sales'));
  for (const d of saleSnap.docs) {
    const sale = { id: d.id, ...d.data() } as SaleInvoice;
    const saleDate = sale.saleDate || sale.dueDate || '';
    // Re-billed charges ride in totalAmountUSD but are posted as their own
    // sale_expense entries (Dr AR) below — revenue here is goods only.
    // Indian GST rides in totalAmountUSD too, posted to Output GST.
    const saleTax = sale.taxTotalUSD ?? 0;
    const goodsUSD = round2((sale.totalAmountUSD ?? 0) - (sale.chargesUSD ?? 0) - saleTax);
    if (onOrAfter(saleDate) && goodsUSD > 0.005) {
      push('sales_invoice', sale.id, 'sales', saleDate || start || today(),
        linesSaleInvoice(goodsUSD, saleCogs(sale.lineItems ?? []), sale.toEntityId, saleTax),
        `Sale ${sale.invoiceNo}`);
    }
    if (sale.brokerEntityId && (sale.brokerCommissionUSD ?? 0) > 0.005 && onOrAfter(saleDate)) {
      push('broker_accrual', sale.id, 'sales', saleDate || start || today(),
        linesBrokerAccrual(sale.brokerCommissionUSD!, sale.brokerEntityId), `Broker commission — ${sale.invoiceNo}`);
    }
    // Per-line Broker1/Broker2 accruals — this is the ONLY broker
    // accounting a sale gets now (the header broker was removed); mirrors
    // Sales.tsx's own posting.
    for (const [brokerEntityId, commissionUSD] of lineBrokerCommissions(sale.lineItems ?? [])) {
      if (commissionUSD <= 0.005 || !onOrAfter(saleDate)) continue;
      push('broker_accrual', sale.id, 'sales', saleDate || start || today(),
        linesBrokerAccrual(commissionUSD, brokerEntityId), `Broker commission (per-line) — ${sale.invoiceNo}`,
        lineBrokerAccrualId(sale.id, brokerEntityId));
    }
    const paySnap = await getDocs(collection(db, 'sales', sale.id, 'payments'));
    for (const p of paySnap.docs) {
      const pay = { id: p.id, ...p.data() } as SalePayment;
      const amt = pay.amount ?? 0;
      if (!pay.bankAccountId || amt <= 0.005 || !onOrAfter(pay.paidDate)) continue;
      push('ar_payment', pay.id, `sales/${sale.id}/payments`, pay.paidDate || saleDate || today(),
        linesArPayment(amt, sale.toEntityId, pay.bankAccountId), `Receipt for ${sale.invoiceNo}`);
    }
    const bpaySnap = await getDocs(collection(db, 'sales', sale.id, 'brokerPayments'));
    for (const p of bpaySnap.docs) {
      const pay = { id: p.id, ...p.data() } as BrokerPayment;
      const amt = pay.amount ?? 0;
      // Same per-line-first-then-header fallback as the purchase side above.
      const payBrokerEntityId = pay.brokerEntityId ?? sale.brokerEntityId;
      if (!pay.bankAccountId || amt <= 0.005 || !payBrokerEntityId || !onOrAfter(pay.paidDate)) continue;
      push('broker_payment', pay.id, `sales/${sale.id}/brokerPayments`, pay.paidDate || saleDate || today(),
        linesBrokerPayment(amt, payBrokerEntityId, pay.bankAccountId, brokerGstOf(pay)), `Broker payout — ${sale.invoiceNo}`);
    }
    // Small differences settled on the sale (FX Loss).
    const sadjSnap = await getDocs(collection(db, 'sales', sale.id, 'adjustments'));
    for (const a of sadjSnap.docs) {
      const adj = { id: a.id, ...a.data() } as { id: string; date: string; amountUSD?: number; kind?: string };
      if (adj.kind !== 'settle' || (adj.amountUSD ?? 0) <= 0.005 || !onOrAfter(adj.date)) continue;
      push('sale_settle', adj.id, `sales/${sale.id}/adjustments`, adj.date || saleDate || today(),
        linesSaleSettle(adj.amountUSD!, sale.toEntityId), `Settled difference on ${sale.invoiceNo}`);
    }
  }

  // 2b — purchase returns.
  const prSnap = await getDocs(collection(db, 'purchaseReturns'));
  for (const d of prSnap.docs) {
    const pr = { id: d.id, ...d.data() } as { id: string; date: string; totalCostUSD?: number; taxTotalUSD?: number; fromEntityId: string; returnNo?: string };
    if ((pr.totalCostUSD ?? 0) <= 0.005 || !onOrAfter(pr.date)) continue;
    push('purchase_return', pr.id, 'purchaseReturns', pr.date || today(),
      linesPurchaseReturn(pr.totalCostUSD!, pr.fromEntityId, pr.taxTotalUSD ?? 0), `Return ${pr.returnNo ?? ''}`);
  }
  const srSnap = await getDocs(collection(db, 'salesReturns'));
  for (const d of srSnap.docs) {
    const sr = { id: d.id, ...d.data() } as { id: string; date: string; totalRevenueUSD?: number; totalCostUSD?: number; taxTotalUSD?: number; toEntityId: string; returnNo?: string };
    if (((sr.totalRevenueUSD ?? 0) <= 0.005 && (sr.totalCostUSD ?? 0) <= 0.005) || !onOrAfter(sr.date)) continue;
    push('sales_return', sr.id, 'salesReturns', sr.date || today(),
      linesSaleReturn(sr.totalRevenueUSD ?? 0, sr.totalCostUSD ?? 0, sr.toEntityId, sr.taxTotalUSD ?? 0), `Credit note ${sr.returnNo ?? ''}`);
  }

  // 2c — supplier / customer advances + their applications.
  const saSnap = await getDocs(collection(db, 'supplierAdvances'));
  for (const d of saSnap.docs) {
    const a = { id: d.id, ...d.data() } as { id: string; date: string; amountUSD?: number; fromEntityId: string; bankAccountId?: string; advanceNo?: string };
    if ((a.amountUSD ?? 0) > 0.005 && a.bankAccountId && onOrAfter(a.date)) {
      push('supplier_advance', a.id, 'supplierAdvances', a.date || today(),
        linesSupplierAdvance(a.amountUSD!, a.fromEntityId, a.bankAccountId), `Advance ${a.advanceNo ?? ''}`);
    }
    const apps = await getDocs(collection(db, 'supplierAdvances', a.id, 'applications'));
    for (const p of apps.docs) {
      const ap = { id: p.id, ...p.data() } as { id: string; date: string; amountUSD?: number; invoiceNo?: string };
      if ((ap.amountUSD ?? 0) <= 0.005 || !onOrAfter(ap.date)) continue;
      push('supplier_advance_apply', ap.id, `supplierAdvances/${a.id}/applications`, ap.date || a.date || today(),
        linesSupplierAdvanceApply(ap.amountUSD!, a.fromEntityId), `Advance applied to ${ap.invoiceNo ?? ''}`);
    }
  }
  const caSnap = await getDocs(collection(db, 'customerAdvances'));
  for (const d of caSnap.docs) {
    const a = { id: d.id, ...d.data() } as { id: string; date: string; amountUSD?: number; toEntityId: string; bankAccountId?: string; advanceNo?: string };
    if ((a.amountUSD ?? 0) > 0.005 && a.bankAccountId && onOrAfter(a.date)) {
      push('customer_advance', a.id, 'customerAdvances', a.date || today(),
        linesCustomerAdvance(a.amountUSD!, a.toEntityId, a.bankAccountId), `Advance ${a.advanceNo ?? ''}`);
    }
    const apps = await getDocs(collection(db, 'customerAdvances', a.id, 'applications'));
    for (const p of apps.docs) {
      const ap = { id: p.id, ...p.data() } as { id: string; date: string; amountUSD?: number; invoiceNo?: string };
      if ((ap.amountUSD ?? 0) <= 0.005 || !onOrAfter(ap.date)) continue;
      push('customer_advance_apply', ap.id, `customerAdvances/${a.id}/applications`, ap.date || a.date || today(),
        linesCustomerAdvanceApply(ap.amountUSD!, a.toEntityId), `Advance applied to ${ap.invoiceNo ?? ''}`);
    }
  }

  // 2d — opening stock batches + stock adjustments / write-offs.
  const osSnap = await getDocs(collection(db, 'openingStock'));
  for (const d of osSnap.docs) {
    const b = { id: d.id, ...d.data() } as { id: string; date: string; totalCostUSD?: number; batchNo?: string };
    if ((b.totalCostUSD ?? 0) <= 0.005 || !onOrAfter(b.date)) continue;
    push('opening_stock', b.id, 'openingStock', b.date || today(),
      linesOpeningStock(b.totalCostUSD!), `Opening stock ${b.batchNo ?? ''}`);
  }
  const adjSnap = await getDocs(collection(db, 'stockAdjustments'));
  for (const d of adjSnap.docs) {
    const a = { id: d.id, ...d.data() } as { id: string; kind: 'loss' | 'gain' | 'writeoff'; date: string; costDelta?: number; adjustmentNo?: string; shape?: string };
    const c = Math.abs(a.costDelta ?? 0);
    if (c <= 0.005 || !onOrAfter(a.date)) continue;
    const lines = a.kind === 'gain' ? linesStockGain(c) : a.kind === 'writeoff' ? linesStockWriteoff(c) : linesStockLoss(c);
    push(a.kind === 'writeoff' ? 'stock_writeoff' : 'stock_adjustment', a.id, 'stockAdjustments', a.date || today(),
      lines, `${a.kind === 'writeoff' ? 'Write-off' : 'Stock adjustment'} ${a.adjustmentNo ?? ''} (${a.shape ?? ''})`);
  }
  const revalSnap = await getDocs(collection(db, 'stockRevaluations'));
  for (const d of revalSnap.docs) {
    const r = { id: d.id, ...d.data() } as { id: string; date: string; deltaUSD?: number; revaluationNo?: string; shape?: string };
    const delta = round2(r.deltaUSD ?? 0);
    if (Math.abs(delta) <= 0.005 || !onOrAfter(r.date)) continue;
    push('stock_revaluation', r.id, 'stockRevaluations', r.date || today(),
      linesStockRevaluation(delta), `Revaluation ${r.revaluationNo ?? ''} (${r.shape ?? ''})`);
  }

  // 2e — supplier memos (inbound consignment). One entry per memo, sized to
  // whatever is still held; the per-line purchase conversions ride in via the
  // ordinary `invoices` pass above.
  const smSnap = await getDocs(collection(db, 'supplierMemos'));
  for (const d of smSnap.docs) {
    const m = { id: d.id, ...d.data() } as { id: string; date: string; heldCostUSD?: number; memoNo?: string };
    const held = round2(m.heldCostUSD ?? 0);
    if (held <= 0.005 || !onOrAfter(m.date)) continue;
    push('memo_in', m.id, 'supplierMemos', m.date || today(),
      linesMemoIn(held), `Memo in ${m.memoNo ?? ''}`);
  }

  // 2f — customer memos (outbound consignment). Unlike memo-in, this is a
  // point-in-time snapshot, not a running "still held" figure — the entry
  // exists at its original totalCostUSD for as long as the memo is 'open',
  // and is removed entirely (not resized) once manually closed.
  const cmSnap = await getDocs(collection(db, 'customerMemos'));
  for (const d of cmSnap.docs) {
    const m = { id: d.id, ...d.data() } as { id: string; date: string; totalCostUSD?: number; memoNo?: string; status?: string };
    const cost = round2(m.totalCostUSD ?? 0);
    if (m.status !== 'open' || cost <= 0.005 || !onOrAfter(m.date)) continue;
    push('memo_out', m.id, 'customerMemos', m.date || today(),
      linesMemoOut(cost), `Memo out ${m.memoNo ?? ''}`);
  }

  // 3 — operating expenses (bill-style docs).
  const expSnap = await getDocs(collection(db, 'expenses'));
  for (const d of expSnap.docs) {
    const exp = { id: d.id, ...d.data() } as { id: string; category: string; amountUSD: number; date: string; paymentStatus?: string; bankAccountId?: string; vendorEntityId?: string; description?: string } & PaidGstLike;
    if ((exp.amountUSD ?? 0) <= 0.005 || !onOrAfter(exp.date)) continue;
    const paid = exp.paymentStatus === 'paid' && exp.bankAccountId;
    push('expense', exp.id, 'expenses', exp.date || today(),
      linesExpense(exp.amountUSD, exp.category, paid ? { bankAccountId: exp.bankAccountId! } : 'accrued', exp.vendorEntityId,
        paidGstPosting(exp)),
      exp.description || `Expense — ${exp.category}`);
  }

  // 3b — purchase-side costs (freight / duty / certification).
  const peSnap = await getDocs(collection(db, 'purchaseExpenses'));
  for (const d of peSnap.docs) {
    const pe = { id: d.id, ...d.data() } as { id: string; amountUSD?: number; date: string; treatment?: string; paymentStatus?: string; bankAccountId?: string; vendorEntityId?: string; description?: string; expenseNo?: string };
    if ((pe.amountUSD ?? 0) <= 0.005 || !onOrAfter(pe.date)) continue;
    const paid = pe.paymentStatus === 'paid' && pe.bankAccountId;
    push('purchase_expense', pe.id, 'purchaseExpenses', pe.date || today(),
      linesPurchaseExpense(pe.amountUSD!, pe.treatment === 'expense' ? 'expense' : 'capitalise',
        paid ? { bankAccountId: pe.bankAccountId! } : 'accrued', pe.vendorEntityId),
      pe.description || `Purchase expense ${pe.expenseNo ?? ''}`);
  }

  // 3c — sale-side costs (shipping / insurance / export duty).
  const seSnap = await getDocs(collection(db, 'saleExpenses'));
  for (const d of seSnap.docs) {
    const se = { id: d.id, ...d.data() } as { id: string; amountUSD?: number; date: string; chargeToCustomer?: boolean; customerEntityId: string; paymentStatus?: string; bankAccountId?: string; vendorEntityId?: string; description?: string; expenseNo?: string };
    if ((se.amountUSD ?? 0) <= 0.005 || !onOrAfter(se.date)) continue;
    const paid = se.paymentStatus === 'paid' && se.bankAccountId;
    push('sale_expense', se.id, 'saleExpenses', se.date || today(),
      linesSaleExpense(se.amountUSD!, !!se.chargeToCustomer, paid ? { bankAccountId: se.bankAccountId! } : 'accrued',
        se.customerEntityId, se.vendorEntityId),
      se.description || `Sale expense ${se.expenseNo ?? ''}`);
  }

  // 4 — standalone bank cash-book rows: opening balances, income, and legacy
  // expense entries (the ones with no linked `expenses` doc). AP/AR/broker
  // payment rows are already covered per-invoice above, so they're skipped.
  const btSnap = await getDocs(collection(db, 'bankTransactions'));
  const transferLegs = new Map<string, { out?: { accountId: string; amount: number; date: string; description?: string }; in?: { accountId: string } }>();
  for (const d of btSnap.docs) {
    const t = { id: d.id, ...d.data() } as { id: string; referenceType?: string; referenceId?: string; accountId?: string; amount?: number; date?: string; category?: string; description?: string; direction?: string; transferGroupId?: string } & PaidGstLike;
    const amt = t.amount ?? 0;
    if (!t.accountId || amt <= 0.005 || !onOrAfter(t.date)) continue;
    if (t.referenceType === 'opening') {
      push('opening_balance', t.id, 'bankTransactions', t.date || today(),
        linesOpeningBank(amt, t.accountId), t.description || 'Opening balance');
    } else if (t.referenceType === 'income') {
      push('income', t.id, 'bankTransactions', t.date || today(),
        linesIncome(amt, t.accountId), t.description || `Income — ${t.category || ''}`);
    } else if (t.referenceType === 'expense' && !t.referenceId) {
      push('expense', t.id, 'bankTransactions', t.date || today(),
        linesExpense(amt, t.category || '', { bankAccountId: t.accountId }, undefined, paidGstPosting(t)),
        t.description || `Expense — ${t.category || ''}`);
    } else if (t.referenceType === 'deposit') {
      push('bank_deposit', t.id, 'bankTransactions', t.date || today(),
        linesBankDeposit(amt, t.accountId), t.description || 'Cash deposit');
    } else if (t.referenceType === 'withdrawal') {
      push('bank_withdrawal', t.id, 'bankTransactions', t.date || today(),
        linesBankWithdrawal(amt, t.accountId), t.description || 'Cash withdrawal');
    } else if (t.referenceType === 'bank_charge') {
      push('bank_charge', t.id, 'bankTransactions', t.date || today(),
        linesBankCharge(amt, t.accountId), t.description || 'Bank charge');
    } else if (t.referenceType === 'transfer' && t.transferGroupId) {
      const g = transferLegs.get(t.transferGroupId) ?? {};
      if (t.direction === 'out') g.out = { accountId: t.accountId, amount: amt, date: t.date || today(), description: t.description };
      else g.in = { accountId: t.accountId };
      transferLegs.set(t.transferGroupId, g);
    }
  }
  for (const [groupId, g] of transferLegs) {
    if (!g.out || !g.in) continue;
    push('bank_transfer', groupId, 'bankTransactions', g.out.date,
      linesBankTransfer(g.out.amount, g.out.accountId, g.in.accountId), g.out.description || 'Transfer between accounts');
  }

  // 5 — write in batches of 400 (Firestore hard limit is 500).
  let totalDebit = 0, totalCredit = 0;
  for (let i = 0; i < planned.length; i += 400) {
    const batch = writeBatch(db);
    for (const pe of planned.slice(i, i + 400)) {
      batch.set(doc(db, 'journalEntries', pe.id), stripUndef({ ...pe.entry, id: pe.id }));
      for (const l of pe.entry.lines) { totalDebit += l.debit; totalCredit += l.credit; }
    }
    await batch.commit();
  }

  totalDebit = round2(totalDebit);
  totalCredit = round2(totalCredit);
  return {
    entries: planned.length,
    totalDebit, totalCredit,
    balanced: Math.abs(totalDebit - totalCredit) < 0.01,
    seeded: seed.created,
  };
}

function today() { return new Date().toISOString().slice(0, 10); }

// ── Report helper (used by Chart of Accounts now; more in Phase A3) ────────

/**
 * Net movement per account across a set of entries, expressed in the
 * account's natural sign: debit accounts positive on net-debit, credit
 * accounts positive on net-credit. `normalById` maps accountId → normalBalance.
 */
export function accountBalances(
  entries: Pick<JournalEntry, 'lines'>[],
  normalById: Record<string, NormalBalance>,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const e of entries) {
    for (const l of e.lines) {
      const net = (l.debit || 0) - (l.credit || 0);
      const signed = (normalById[l.accountId] ?? 'debit') === 'debit' ? net : -net;
      out[l.accountId] = round2((out[l.accountId] ?? 0) + signed);
    }
  }
  return out;
}
